import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaCog } from 'react-icons/fa';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_SERVER_URL || '';

export default function SpeedTestKillSwitchDropdown() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({
    manual_enabled: true,
    auto_enabled: true,
    auto_interval_hours: 24,
  });
  const [intervalInput, setIntervalInput] = useState('24');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [todayCount, setTodayCount] = useState(null);
  const ref = useRef(null);

  const fetchSettings = async () => {
    if (!API_BASE) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/maiApi/speed-test/settings`);
      const s = res?.data?.settings;
      if (s) {
        setSettings({
          manual_enabled: s.manual_enabled !== false,
          auto_enabled: s.auto_enabled !== false,
          auto_interval_hours: typeof s.auto_interval_hours === 'number' ? s.auto_interval_hours : 24,
        });
        setIntervalInput(String(s.auto_interval_hours ?? 24));
      }
    } catch (e) {
      console.error('Failed to fetch speed test settings:', e);
      setError('Could not load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchTodayCount = async () => {
    if (!API_BASE) return;
    try {
      const today = new Date().toISOString().slice(0, 10);
      const res = await axios.get(`${API_BASE}/maiApi/speed-test/stats/by-day`, {
        params: { from: today, to: today },
      });
      const day = (res?.data?.days || []).find((d) => d.date === today);
      setTodayCount(day ? day.successful_count : 0);
    } catch (_) {
      setTodayCount(null);
    }
  };

  useEffect(() => {
    if (open) {
      fetchSettings();
      fetchTodayCount();
    }
  }, [open]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateSetting = async (key, value) => {
    if (!API_BASE) return;
    setSaving(true);
    setError(null);
    try {
      const res = await axios.put(`${API_BASE}/maiApi/speed-test/settings`, { [key]: value });
      const s = res?.data?.settings;
      if (s) {
        setSettings({
          manual_enabled: s.manual_enabled !== false,
          auto_enabled: s.auto_enabled !== false,
          auto_interval_hours: typeof s.auto_interval_hours === 'number' ? s.auto_interval_hours : 24,
        });
        if (key === 'auto_interval_hours') setIntervalInput(String(s.auto_interval_hours ?? 24));
      } else {
        setSettings((prev) => ({ ...prev, [key]: value }));
      }
    } catch (e) {
      console.error('Failed to update speed test settings:', e);
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveIntervalHours = () => {
    const h = parseInt(intervalInput, 10);
    if (Number.isFinite(h) && h >= 1 && h <= 168) {
      updateSetting('auto_interval_hours', h);
    } else {
      setIntervalInput(String(settings.auto_interval_hours ?? 24));
    }
  };

  const toggle = (key) => {
    const next = !settings[key];
    updateSetting(key, next);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label="Speed test settings"
        className={`px-5 py-3 mx-1 text-sm font-medium rounded-t-lg transition-all duration-200 focus:outline-none ${
          open
            ? 'bg-white text-gray-900 border-t-2 border-blue-500 shadow-sm'
            : 'text-gray-300 hover:text-white hover:bg-gray-800'
        }`}
        onClick={() => setOpen(prev => !prev)}
      >
        <FaCog className="w-4 h-4 inline-block" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-white text-gray-900 rounded-lg shadow-lg border border-gray-200 z-20 py-2">
          <div className="px-3 pb-2 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-800">Kill Switch</h3>
            <p className="text-xs text-gray-500 mt-0.5">ON = devices can run speed tests · OFF = disabled</p>
          </div>
          {error && <div className="px-3 py-1.5 text-xs text-red-600">{error}</div>}
          <div className="px-3 py-2 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-gray-700">Manual</span>
              <button
                type="button"
                role="switch"
                aria-checked={!!settings.manual_enabled}
                disabled={loading || saving}
                onClick={() => toggle('manual_enabled')}
                className={`relative inline-flex h-6 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 ${
                  settings.manual_enabled ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                    settings.manual_enabled ? 'translate-x-4' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-gray-700">Auto schedule</span>
              <button
                type="button"
                role="switch"
                aria-checked={!!settings.auto_enabled}
                disabled={loading || saving}
                onClick={() => toggle('auto_enabled')}
                className={`relative inline-flex h-6 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 ${
                  settings.auto_enabled ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                    settings.auto_enabled ? 'translate-x-4' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between gap-2">
              <label className="text-sm text-gray-700" htmlFor="auto-interval-hours">
                Auto interval (hours)
              </label>
              <div className="flex items-center gap-1">
                <input
                  id="auto-interval-hours"
                  type="number"
                  min={1}
                  max={168}
                  value={intervalInput}
                  onChange={(e) => setIntervalInput(e.target.value)}
                  onBlur={saveIntervalHours}
                  onKeyDown={(e) => e.key === 'Enter' && saveIntervalHours()}
                  disabled={loading || saving}
                  className="w-14 rounded border border-gray-300 px-1.5 py-0.5 text-sm text-gray-800"
                />
                <button
                  type="button"
                  onClick={saveIntervalHours}
                  disabled={loading || saving}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Save
                </button>
              </div>
            </div>
            {todayCount !== null && (
              <div className="text-xs text-gray-500">
                Successful speed tests today: <span className="font-medium text-gray-700">{todayCount}</span>
              </div>
            )}
          </div>
          {(loading || saving) && (
            <div className="px-3 py-1.5 text-xs text-gray-500">{loading ? 'Loading…' : 'Saving…'}</div>
          )}
          <div className="px-3 pt-2 border-t border-gray-100">
            <Link
              to="/speedtest-dashboard"
              className="text-xs text-blue-600 hover:text-blue-800"
              onClick={() => setOpen(false)}
            >
              View Speed Test dashboard →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
